import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../api/admin";
import { usersApi } from "../../api/users";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

function MetricCard({ icon, title, value, helper }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: { xs: 3, sm: 4 },
        border: "1px solid rgba(25,36,47,0.08)",
        height: "100%",
        minWidth: 0,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 }, "&:last-child": { pb: { xs: 2, sm: 2.5, md: 3 } } }}>
        <Stack direction="row" spacing={{ xs: 1.35, sm: 2 }} alignItems="center">
          <Box
            sx={{
              width: { xs: 42, sm: 48 },
              height: { xs: 42, sm: 48 },
              borderRadius: { xs: 2.5, sm: 3 },
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              bgcolor: "rgba(243,90,58,0.1)",
              color: BRAND.orange,
              "& svg": { fontSize: { xs: 23, sm: 24 } },
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "rgba(25,36,47,0.62)", fontSize: { xs: ".8rem", sm: ".9rem" }, fontWeight: 850 }}>
              {title}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                mt: 0.1,
                color: BRAND.dark,
                fontSize: { xs: "1.55rem", sm: "1.8rem", md: "2.05rem" },
                lineHeight: 1.1,
                fontWeight: 950,
                overflowWrap: "anywhere",
              }}
            >
              {value}
            </Typography>
          </Box>
        </Stack>
        {helper && (
          <Typography sx={{ mt: { xs: 1.25, sm: 2 }, color: "rgba(25,36,47,0.62)", fontSize: { xs: ".78rem", sm: ".9rem" }, lineHeight: 1.5 }}>
            {helper}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function SuperPanelPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [approvingId, setApprovingId] = React.useState("");

  const loadOverview = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await adminApi.overview();
      setOverview(data.overview);
    } catch (err) {
      setError(err.message || "No se pudo cargar el superpanel");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const approveAsClient = async (user) => {
    try {
      setApprovingId(user.id);
      setError("");
      await usersApi.approve(user.id, { globalRole: "client" });
      await loadOverview();
    } catch (err) {
      setError(err.message || "No se pudo aprobar el usuario");
    } finally {
      setApprovingId("");
    }
  };

  const totals = overview?.totals || {};

  return (
    <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ minWidth: 0 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={{ xs: 1.5, md: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h3"
            sx={{
              color: BRAND.dark,
              fontSize: { xs: "1.85rem", sm: "2.45rem", md: "3rem" },
              lineHeight: 1.06,
              letterSpacing: { xs: "-.8px", sm: "-1px" },
              fontWeight: 950,
            }}
          >
            Superpanel WebProfit
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.6, fontSize: { xs: ".9rem", sm: "1rem" }, lineHeight: 1.5 }}>
            Tu vista interna para aprobar clientes, controlar proyectos y ordenar el onboarding.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: { xs: "100%", md: "auto" }, flexShrink: 0 }}
        >
          <Button
            onClick={() => navigate("/dashboard/actividad")}
            variant="outlined"
            sx={{ width: { xs: "100%", sm: "auto" }, minHeight: 42, borderRadius: 999, textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
          >
            Actividad global
          </Button>
          <Button
            onClick={() => navigate("/dashboard/usuarios")}
            variant="outlined"
            sx={{ width: { xs: "100%", sm: "auto" }, minHeight: 42, borderRadius: 999, textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
          >
            Usuarios
          </Button>
          <Button
            onClick={() => navigate("/dashboard/proyectos")}
            variant="contained"
            sx={{ width: { xs: "100%", sm: "auto" }, minHeight: 42, bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
          >
            Proyectos
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>}
      {loading && <Alert severity="info" sx={{ borderRadius: 3 }}>Cargando superpanel...</Alert>}

      <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
        <Grid item xs={12} md={3}>
          <MetricCard icon={<PeopleAltRoundedIcon />} title="Usuarios" value={loading ? "..." : totals.users || 0} helper="Todos los usuarios registrados en WebProfit." />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard icon={<PendingActionsRoundedIcon />} title="Pendientes" value={loading ? "..." : totals.pendingUsers || 0} helper="Cuentas esperando aprobación." />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard icon={<StorefrontRoundedIcon />} title="Proyectos" value={loading ? "..." : totals.projects || 0} helper="Clientes/páginas creadas." />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard icon={<CheckCircleRoundedIcon />} title="Activos" value={loading ? "..." : totals.activeProjects || 0} helper="Proyectos operativos." />
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        <Grid item xs={12} lg={6}>
          <Card elevation={0} sx={{ borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(25,36,47,0.08)", height: "100%", minWidth: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 }, "&:last-child": { pb: { xs: 2, sm: 2.5, md: 3 } } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontSize: { xs: "1.15rem", sm: "1.35rem" }, lineHeight: 1.2, fontWeight: 950, color: BRAND.dark }}>Usuarios pendientes</Typography>
                  <Typography sx={{ color: "rgba(25,36,47,0.62)", mt: 0.45, fontSize: { xs: ".82rem", sm: ".95rem" }, lineHeight: 1.45 }}>Primero confirman email, después vos los aprobás como cliente.</Typography>
                </Box>
                <Button
                  onClick={() => navigate("/dashboard/usuarios")}
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{ alignSelf: { xs: "flex-start", sm: "center" }, color: BRAND.dark, textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
                >
                  Ver todos
                </Button>
              </Stack>

              <Stack spacing={1.5}>
                {(overview?.pendingUsers || []).length === 0 && <Alert severity="success">No hay usuarios pendientes.</Alert>}
                {(overview?.pendingUsers || []).map((user) => (
                  <Box key={user.id} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: "#F6F7FB", border: "1px solid rgba(25,36,47,0.06)", minWidth: 0 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark, overflowWrap: "anywhere" }}>{user.name}</Typography>
                        <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)", overflowWrap: "anywhere" }}>{user.email}</Typography>
                      </Box>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} sx={{ flexShrink: 0 }}>
                        <Chip
                          size="small"
                          label={user.emailVerified ? "Email ok" : "Sin verificar"}
                          color={user.emailVerified ? "success" : "warning"}
                          sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
                        />
                        <Button
                          disabled={approvingId === user.id}
                          onClick={() => approveAsClient(user)}
                          variant="contained"
                          sx={{ width: { xs: "100%", sm: "auto" }, bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                        >
                          Aprobar
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card elevation={0} sx={{ borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(25,36,47,0.08)", height: "100%", minWidth: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 }, "&:last-child": { pb: { xs: 2, sm: 2.5, md: 3 } } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontSize: { xs: "1.15rem", sm: "1.35rem" }, lineHeight: 1.2, fontWeight: 950, color: BRAND.dark }}>Últimos proyectos</Typography>
                  <Typography sx={{ color: "rgba(25,36,47,0.62)", mt: 0.45, fontSize: { xs: ".82rem", sm: ".95rem" }, lineHeight: 1.45 }}>Páginas/clientes cargados en el sistema.</Typography>
                </Box>
                <Button
                  onClick={() => navigate("/dashboard/proyectos")}
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{ alignSelf: { xs: "flex-start", sm: "center" }, color: BRAND.dark, textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
                >
                  Ver todos
                </Button>
              </Stack>

              <Stack spacing={1.5}>
                {(overview?.latestProjects || []).length === 0 && <Alert severity="info">Todavía no hay proyectos.</Alert>}
                {(overview?.latestProjects || []).map((project) => (
                  <Box key={project.id} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: "#F6F7FB", border: "1px solid rgba(25,36,47,0.06)", minWidth: 0 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.25}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark, overflowWrap: "anywhere" }}>{project.name}</Typography>
                        <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)", overflowWrap: "anywhere" }}>/{project.slug} · {project.plan} · {project.status}</Typography>
                      </Box>
                      <Button
                        onClick={() => navigate(`/dashboard/proyectos/${project.slug}`)}
                        sx={{ width: { xs: "100%", sm: "auto" }, alignSelf: { xs: "stretch", sm: "center" }, color: BRAND.orange, textTransform: "none", fontWeight: 900 }}
                      >
                        Administrar
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
